import React from "react";
import { Card } from "primereact/card";
import { useTranslation } from "react-i18next";

interface PeriodStatsProps {
	label: string;
	averageSeverity: string;
	avg: number | string;
	min: number | string;
	max: number | string;
	readings: number;
	type: string;
}

interface TrendCardProps {
	title: string;
	period1: PeriodStatsProps;
	period2: PeriodStatsProps;
	description: React.ReactNode;
	descriptionColor?: string;
	limitedData?: string;
}

import type { TFunction } from "i18next";

const getUnit = (type: string, value: string | number, t: TFunction): string => {
	if (type.includes("GLUCOSE") && value !== "-") {
		return t("PARTICIPANT.BIOMETRICS.TREND_CARD.PTS_GLUCOSE");
	}
	if (type === "WEIGHT" && value !== "-") {
		return t("PARTICIPANT.BIOMETRICS.TREND_CARD.PTS_WEIGHT");
	}
	return "";
};

const PeriodStats: React.FC<PeriodStatsProps> = ({
	label,
	averageSeverity,
	avg,
	min,
	max,
	readings,
	type,
}) => {
	const { t } = useTranslation("common");

	return (
		<div className="trend-card__period-stats">
			<p className="fw-bold mb-1">{label}</p>
			{/* class "avg-${averageSeverity}" is applied only if type is not WEIGHT */}
			<p className={"stat" + (type !== "WEIGHT" ? " avg-" + averageSeverity : "")}>
				{t("PARTICIPANT.BIOMETRICS.CHARTS.INFO_AVG", {
					value: avg,
				})}{" "}
				{getUnit(type, avg, t)}
			</p>
			<p className="stat small">
				{t("PARTICIPANT.BIOMETRICS.CHARTS.INFO_MIN", {
					value: min,
				})}{" "}
				{getUnit(type, min, t)}
			</p>

			<p className="stat small">
				{t("PARTICIPANT.BIOMETRICS.CHARTS.INFO_MAX", {
					value: max,
				})}{" "}
				{getUnit(type, max, t)}
			</p>
			<p className="stat small">
				{t("PARTICIPANT.BIOMETRICS.CHARTS.INFO_READINGS", {
					value: readings,
				})}
			</p>
		</div>
	);
};

export const TrendCard: React.FC<TrendCardProps> = ({
	title,
	period1,
	period2,
	description,
	descriptionColor,
	limitedData,
}) => {
	return (
		<Card className="trend-card">
			<p className="trend-card__title">{title}</p>

			<div className="trend-card__stats">
				<div className="trend-card__stats-half trend-card__stats-half--left">
					<PeriodStats {...period1} />
				</div>

				<div className="trend-card__stats-half">
					<PeriodStats {...period2} />
				</div>
			</div>

			<p className={`trend-card__description ${descriptionColor || ""}`}>{description}</p>

			{limitedData && (
				<div className="trend-card__limited-data">
					<i className="pi pi-exclamation-triangle" />
					<span className="small"> {limitedData}</span>
				</div>
			)}
		</Card>
	);
};
